/** null 안전 표시 헬퍼. 레거시 테이블이 빈 칸을 그대로 두던 자리를 '-' 로 채운다. */
export function text(value: string | null | undefined): string {
  return value === null || value === undefined || value === '' ? '-' : value;
}

/** maxLen 을 넘으면 잘라내고 '...' 을 붙인다 (긴 회원 ID 가 칼럼을 늘리는 걸 막을 때 등). */
export function truncate(value: string | null | undefined, maxLen: number): string {
  if (value === null || value === undefined || value === '') return '-';
  return value.length > maxLen ? `${value.slice(0, maxLen)}...` : value;
}

/**
 * `<a href>` 에 넣어도 되는 주소만 통과시킨다. 아니면 null.
 *
 * 이게 필요한 이유: 경품 썸네일 주소는 **관리자가 직접 타이핑하는 자유 입력**이다
 * (경품 등록 폼의 `placeholder="https://..."`). 거기에 `javascript:alert(1)` 을
 * 적어 저장하면 DB 에 그대로 들어가고, 경품 목록에서 그 썸네일을 누른 다른
 * 관리자의 브라우저에서 실행된다 - 저장형 XSS 다.
 *
 * 값을 손보지 않고 **판정만** 한다. 통과하면 원래 주소를 그대로 쓰고, 아니면
 * 링크를 아예 만들지 않는다 (깨진 링크를 누르게 하는 것보다 낫다).
 *
 * `new URL()` 로 파싱해서 protocol 을 본다. 정규식으로 접두사만 보면
 * `java\tscript:` 나 `%6Aavascript:` 같은 변형을 놓친다 - 브라우저의 URL
 * 파서와 같은 판단을 쓰는 편이 안전하다.
 */
export function safeExternalUrl(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  try {
    const url = new URL(String(value));
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.toString() : null;
  } catch {
    // 상대경로는 여기서 던진다. 바깥으로 나가는 링크 자리라 상대경로는 받지 않는다.
    return null;
  }
}

/**
 * gname(상품명) 등에 원본이 그대로 심어둔 <img> 아이콘 태그를 걷어내고 글자만
 * 남긴다. 표에서는 Cell.tsx 의 RichText 가 진짜 이미지로 그리지만, 순수
 * 텍스트만 필요한 자리(엑셀 붙여넣기용 상자, 툴팁 등)에서는 태그를 그대로
 * 두면 글자로 노출된다.
 */
export function stripImgTags(value: unknown): string {
  if (value === null || value === undefined || value === '') return '';
  return String(value).replace(/<img\b[^>]*>/gi, '').trim();
}

/**
 * DB 의 정수·DECIMAL 컬럼이 프록시(db_proxy.php)를 거치면 숫자가 아니라
 * 문자열로 온다 (예: bank_list.amount 는 int(10) unsigned 인데 API 응답은
 * "1320000" 이 찍힌다). 문자열째로 toLocaleString 을 부르면
 * String.prototype.toLocaleString 은 그냥 원문을 돌려줘서 콤마가 하나도
 * 안 붙는다 — 그래서 먼저 숫자로 바꾼다.
 */
function asNumber(value: number | string): number {
  return typeof value === 'string' ? Number(value) : value;
}

export function money(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '-';
  const n = asNumber(value);
  if (Number.isNaN(n)) return '-';
  return n.toLocaleString('ja-JP');
}

export function num(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '-';
  const n = asNumber(value);
  if (Number.isNaN(n)) return '-';
  return n.toLocaleString();
}

/**
 * 금액 입력칸에 타이핑하는 중간값을 "10,000" 꼴로 다듬는다.
 * 숫자만 남기므로 서버로는 콤마가 섞인 문자열이 가고, 거기서 원본과 같은
 * 방식(str_replace(",","")) 으로 풀린다 — payment/bank.schemas.ts 의 moneyField.
 * 입금건 추가(BankPage)와 카드건 추가(CardPage)가 같이 쓴다.
 */
export function formatAmountDraft(raw: string): string {
  const digits = raw.replace(/[^0-9]/g, '');
  return digits ? Number(digits).toLocaleString() : '';
}

/**
 * 레거시 테이블은 '값 없음' 을 NULL 이 아니라 0 날짜로 적어 뒀다.
 * 원본 화면들도 이 값을 '-' 로 그렸다 (card_list.php:381 등).
 *
 * 'undefined 00:00:00' 도 같은 '값 없음' 이다 - DATETIME 컬럼의
 * '0000-00-00 00:00:00' 을 mysql2 가 dateStrings 모드(server/src/db/direct.ts)로
 * 읽으면 날짜 부분이 undefined 인 이 문자열이 나온다. 그대로 화면에 내보내면
 * "수정 undefined 00:00:00" 처럼 보인다(카드결제 관리에서 실제로 그랬다).
 */
export function isZeroDate(value: unknown): boolean {
  const raw = String(value ?? '').trim();
  if (!raw || raw === 'null') return true;
  return /^0{4}-0{2}-0{2}( 0{2}:0{2}:0{2})?$/.test(raw) || raw.startsWith('undefined');
}

/** 'YYYY-MM-DD HH:MM:SS' 를 보기 좋게. 파싱 실패하면 원문 그대로. */
export function datetime(value: string | null | undefined): string {
  if (!value || isZeroDate(value)) return '-';
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(value);
  if (!m) return value;
  return `${m[1]}-${m[2]}-${m[3]} ${m[4]}:${m[5]}`;
}

/** datetime() 과 같지만 초까지 남긴다 (입고 이력처럼 같은 분에 여러 건이 겹칠 때). */
export function datetimeWithSeconds(value: string | null | undefined): string {
  if (!value || isZeroDate(value)) return '-';
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/.exec(value);
  if (!m) return value;
  return `${m[1]}-${m[2]}-${m[3]} ${m[4]}:${m[5]}:${m[6]}`;
}

export function dateOnly(value: string | null | undefined): string {
  if (!value || isZeroDate(value)) return '-';
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(value);
  return m ? m[1]! : value;
}

/**
 * 'YYYY-MM-DD' - 브라우저가 있는 곳의 **오늘**.
 *
 * `new Date().toISOString().slice(0, 10)` 은 늘 UTC 라, 한국에서 자정~오전 9시
 * 사이에는 어제 날짜가 나온다. 조회 기간의 기본값으로 쓰면 그 시간대에 접속한
 * 사람에게 오늘 자료가 통째로 빠진 채로 보인다.
 */
export function localDate(at: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`;
}

/** localDate 기준으로 개월 수만큼 뒤로 간 날짜. */
export function localDateMonthsAgo(months: number, at: Date = new Date()): string {
  const d = new Date(at);
  d.setMonth(d.getMonth() - months);
  return localDate(d);
}

/**
 * 보기 좋은 전화번호. 저장값은 숫자만이고 화면에서만 이 모양을 쓴다.
 *
 * 서버 `modules/batch-admin/recipients.logic.ts` 의 formatPhone 과 **같은
 * 규칙이어야 한다** - 같은 번호가 화면과 서버에서 다르게 보이면 대조가 안 된다.
 * (workspace 가 달라 코드를 공유할 수 없어 규칙만 맞춘다.)
 */
export function phone(value: string | null | undefined): string {
  // 값이 없어도 화면이 죽지 않게 한다 - 이 함수 하나가 던지면 그 화면 전체가
  // 빈 화면이 된다(옛 서버에 붙어 필드가 없을 때 실제로 그랬다).
  const n = (value ?? '').replace(/[^0-9]/g, '');
  if (/^01\d{8,9}$/.test(n)) {
    return n.length === 10
      ? `${n.slice(0, 3)}-${n.slice(3, 6)}-${n.slice(6)}`
      : `${n.slice(0, 3)}-${n.slice(3, 7)}-${n.slice(7)}`;
  }
  // 대표번호 15xx·16xx·17xx·18xx (8자리)
  if (/^1[5678]\d{6}$/.test(n)) return `${n.slice(0, 4)}-${n.slice(4)}`;
  // 유선 지역번호
  if (/^0\d{1,2}/.test(n) && n.length >= 9) {
    const area = n.startsWith('02') ? 2 : 3;
    const rest = n.slice(area);
    const mid = rest.length - 4;
    return `${n.slice(0, area)}-${rest.slice(0, mid)}-${rest.slice(mid)}`;
  }
  return n;
}
